import React, { useEffect, useState } from 'react';
import { Row, Col, TabContent, TabPane, Nav, NavItem, NavLink } from 'reactstrap';
import ExamHeading from './ExamHeading';
import ExamsTab from './ExamTabExams';
import QuestionsTab from './ExamTabQuestions';
import AnswerKeyTab from './ExamTabAnswerKey';
import ExamModal from './ExamModal';
import CopyYearModal from './CopyYearModal';
import * as Act from '../../store/actions/actionCreators';
import * as API from '../../store/actions/fetchActions';
import classnames from 'classnames';
import { useDispatch, useSelector } from 'react-redux';

const Exam = () => {
  const [examInput, setExamInput] = useState({});
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(100);
  const dispatch = useDispatch();

  // Redux selectors
  const activeTab = useSelector(state => state.exam.activeTab);
  const modalOpen = useSelector(state => state.exam.modalOpen);
  const year = useSelector(state => state.exam.year);
  const years = useSelector(state => state.exam.years);
  const exam = useSelector(state => state.exam.exam);
  const exams = useSelector(state => state.exam.exams);
  const pagination = useSelector(state => state.exam.pagination);
  // const loading = useSelector(state => state.exam.loading);
  const [loading, setLocalLoading] = useState(false);
  const [editLoading, setEditLoading] = useState(false);


  // Update the cursor using native JS while loading
  useEffect(() => {
    document.body.style.cursor = loading ? 'wait' : 'default';
    return () => { document.body.style.cursor = 'default'; };
  }, [loading]);

  // Helper function to fetch exam data for a given page.
  // Returns a promise so we can wait for the API call to complete.
  const fetchData = (pageToLoad, pageSize) => {
    setLocalLoading(true);
    const selectedYear = year || (years && years[0]);
    return dispatch(API.fetchExams(selectedYear, pageToLoad, pageSize))
    .then(() => {
      setLocalLoading(false);
    })
    .catch(() => {
      setLocalLoading(false);
    });
  }
  // On initial load and when the year changes, reset to page 1 and fetch data.
  useEffect(() => {
    setPage(1);
    fetchData(1, pageSize);
  }, [year, years, dispatch]);

  // Sorting logic (unchanged)
  useEffect(() => {
    if (!exam.sorted) {
      dispatch(Act.sortExamTab('exams', exam.column ? exam.column : 'test_id'));
    }
  }, [exam.sorted, exam.column, dispatch]);

  // Modal toggle function for editing and copying
  const modalToggle = async (e, type, id) => {
    e.preventDefault();
    switch (type) {
      case 'exam': {
        const selectedExam = exams.find(item => item.exam_id === id);
        
        // Show loading indicator for edit
        setEditLoading(true);
        
        // Fetch online exam data from S3 when editing
        let onlineExamData = [];
        if (selectedExam && selectedExam.exam_id > 0) {
          try {
            console.log(`Fetching online exam data for exam ${selectedExam.exam_id}`);
            onlineExamData = await API.fetchOnlineExamData(selectedExam.exam_id, selectedExam.test_id)(dispatch);
            console.log('Fetched online exam data:', onlineExamData);
            
            // Show toast if no online exam data found
            if (!onlineExamData || onlineExamData.length === 0) {
              dispatch(Act.addToast('No online exam data found', 'info', 'info'));
            }
          } catch (error) {
            console.error(`Error fetching online exam data for exam ${selectedExam.exam_id}:`, error);
            onlineExamData = [];
            
            // Show error toast
            dispatch(Act.addToast('Error loading online exam data', 'danger', 'danger'));
          }
        }
        
        // Ensure online_exam_data is always an array
        const safeOnlineExamData = Array.isArray(onlineExamData) ? onlineExamData : [];
        console.log('Setting exam input with online_exam_data:', safeOnlineExamData);
        
        setExamInput({
          ...selectedExam,
          online_exam_data: safeOnlineExamData,
        });
        
        // Hide loading indicator
        setEditLoading(false);
        dispatch(Act.setExamModal({ main: 'exam' }));
        break;
      }
      case 'copy':
        dispatch(Act.setExamModal({ main: 'copy' }));
        break;
      default:
        dispatch(Act.setExamModal({ main: null }));
    }
  };

  // Handler for radio button exam selection
  const handleSelect = event => {
    const choice = parseInt(event.target.value, 10);
    if (choice !== exam.exam_id) {
      dispatch(Act.setExamSelect(choice));
    }
  };

  // Handler for adding a new exam
  const handleAddExam = () => {
    const currentYear = new Date().getFullYear();
    setExamInput({
      exam_id: -1,
      school_year: year || currentYear,
      exam_name: '',
      test_id: 0,
      discipline: '',
      release_year: year || currentYear,
      owned_by: 'PES',
      free_response_quantity: 0,
      free_response_time_limit: null,
      free_response_url: null,
      multiple_choice_quantity: 0,
      multiple_choice_time_limit: null,
      multiple_choice_url: null,
      preset_comments_required: false,
      calculator_enabled: false,
      resources: [],
      online_exam_data: [],
    });
    dispatch(Act.setExamModal({ main: 'exam' }));
  };

  // Handler for year changes
  const onYearChange = evt => {
    dispatch(Act.setExamYear(evt.target.value));
    // The useEffect will handle fetching data when year changes
  };

  const isViewingHistory = years && Math.max(...years) !== year;
  const examTab = activeTab === 'exams';
  const questionTab = activeTab === 'questions';
  const answerTab = activeTab === 'answers';

  // Pagination handlers update the page only after the API call returns
  const handlePrevPage = () => {
    if (page > 1 && !loading) {
      setLocalLoading(true);
      const newPage = page - 1;
      fetchData(newPage, pageSize).then(() => {
        setPage(newPage);
        setLocalLoading(false)
      });
    }
  };

  const handleNextPage = () => {
    if (pagination && page < pagination.total_pages && !loading) {
      setLocalLoading(true);
      const newPage = page + 1;
      fetchData(newPage, pageSize).then(() => {
        setPage(newPage);
        setLocalLoading(false);
      });
    }
  };
  const handlePageSizeChange = (e) => {
    const newPageSize = parseInt(e.target.value, 10);
    setPageSize(newPageSize);
    fetchData(1, newPageSize).then(() => {
      setPage(1);
      setLocalLoading(false);
    });
  }
  return (
    <>
      <ExamHeading
        year={year}
        onChange={onYearChange}
        modalToggle={modalToggle}
        exam={exam}
        handleExam={handleAddExam}
      />
      <Row>
        <Col>
          <Nav tabs>
            <NavItem>
              <NavLink
                className={classnames({ active: examTab })}
                onClick={() => dispatch(Act.setExamTab('exams'))}
              >
                Exams
              </NavLink>
            </NavItem>
            <NavItem>
              <NavLink
                className={classnames({ active: questionTab })}
                onClick={() => dispatch(Act.setExamTab('questions'))}
              >
                FR Questions
              </NavLink>
            </NavItem>
            <NavItem>
              <NavLink
                className={classnames({ active: answerTab })}
                onClick={() => dispatch(Act.setExamTab('answers'))}
              >
                MC Answer Keys
              </NavLink>
            </NavItem>
          </Nav>
          <TabContent activeTab={activeTab}>
            <TabPane tabId="exams">
              {/* While loading, the cursor shows "wait" and no UI change until data is fetched */}
              <ExamsTab
                exam={exam}
                exams={exams}
                canCopy={isViewingHistory}
                onSelect={handleSelect}
                modalToggle={modalToggle}
                editLoading={editLoading}
              />
              <div className="pagination-controls d-flex align-items-center mt-2">
                <button
                  onClick={handlePrevPage}
                  disabled={page <= 1 || loading}
                  className="btn btn-sm btn-outline-primary"
                >
                <span>{loading ? 'Loading...' : "Back"}</span>
                </button>

                <span className="mx-2 d-flex align-items-center">
                Page Size&nbsp;
                <select
                  className="form-control form-control-sm mx-1"
                  value={pageSize}
                  onChange={handlePageSizeChange}
                >
                  <option value={10}>10</option>
                  <option value={20}>20</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                </select>
                  Page&nbsp;
                  <select
                    className="form-control form-control-sm mx-1"
                    value={page}
                    onChange={e => {
                      const newPage = parseInt(e.target.value, 10);
                      setPage(newPage);
                      fetchData(newPage, pageSize);
                    }}
                  >
                    {Array.from(
                      { length: pagination && pagination.total_pages ? pagination.total_pages : 1 },
                      (_, i) => (
                        <option key={i + 1} value={i + 1}>
                          {i + 1}
                        </option>
                      )
                    )}
                  </select>
                  {/* &nbsp; of {pagination ? pagination.total_pages : 1} */}
                </span>
                <button
                  onClick={handleNextPage}
                  disabled={pagination ? page >= pagination.total_pages || loading : true}
                  className="btn btn-sm btn-outline-primary"
                >
                  <span>{loading ? 'Loading...' : "Next"}</span>
                </button>

              </div>
            </TabPane>
            <TabPane tabId="questions">
              <QuestionsTab exam_id={exam.exam_id} />
            </TabPane>
            <TabPane tabId="answers">
              <AnswerKeyTab exam_id={exam.exam_id} />
            </TabPane>
          </TabContent>
        </Col>
      </Row>
      <ExamModal
        isOpen={modalOpen === 'exam'}
        toggle={modalToggle}
        onChange={(name, value) => {
          setExamInput(prev => ({ ...prev, [name]: value }));
        }}
        onSave={data => dispatch(API.saveExam(data))}
        onDelete={data => dispatch(API.deleteExam(data))}
        saving={!!exam.saving}
        deleting={!!exam.deleting}
        {...examInput}
      />
      <CopyYearModal isOpen={modalOpen === 'copy'} toggle={modalToggle} />
      
      {/* Loading overlay for edit operations */}
      {editLoading && (
        <div 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            zIndex: 9998,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center'
          }}
        >
          <div className="bg-white p-4 rounded">
            <div className="spinner-border text-primary" role="status">
              <span className="sr-only">Loading online exam data...</span>
            </div>
            <div className="mt-2 text-center">Loading online exam data...</div>
          </div>
        </div>
      )}
    </>
  );
};

export default Exam;
